import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RotateCcw, Search } from "lucide-react";

import { attendanceService } from "../../services/attendanceService";
import { timetableService } from "../../services/timetableService";
import { useAuthStore } from "../../stores/authStore";
import { todayIso } from "../../utils/dates";
import { Field } from "../FormLayout";
import { errorLine } from "../inventory/InventoryParts";
import { Notice, type NoticeState } from "../register/RecordParts";
import { QUICK_STATUSES, STATUSES, changedRows, registerCounts, registerDraft, schoolDayOfWeek, statusOf, type RegisterEntry, type Status } from "./attendanceLogic";

/** Taking a class's register for a day, or for one lesson; opens on whatever was saved before. */
export function RegisterPanel({ onSaved }: { onSaved: () => void }) {
  const queryClient = useQueryClient();
  const me = useAuthStore((state) => state.user?.id);
  const [date, setDate] = useState(todayIso());
  const [classId, setClassId] = useState(0);
  const [lesson, setLesson] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState<NoticeState>(null);

  const classes = useQuery({ queryKey: ["attendance", "classes"], queryFn: attendanceService.getRegisterClasses, staleTime: 10 * 60_000 });
  const timetable = useQuery({ queryKey: ["attendance", "my-lessons", me], queryFn: () => timetableService.getTeacherTimetable(me!), enabled: !!me, staleTime: 10 * 60_000 });
  const learners = useQuery({ queryKey: ["attendance", "class-learners", classId], queryFn: () => attendanceService.getClassLearners(classId), enabled: classId > 0 });
  const saved = useQuery({
    queryKey: ["attendance", "register", classId, date, lesson],
    queryFn: () => attendanceService.getAttendanceRecords({ current_class: classId, date, lesson: lesson ? String(lesson) : "day" }),
    enabled: classId > 0,
  });

  // A lone class is the one to take.
  useEffect(() => {
    if (!classId && classes.data?.length === 1) setClassId(classes.data[0].id);
  }, [classes.data, classId]);

  const lessons = useMemo(
    () => (timetable.data ?? []).filter((slot) => slot.is_active && slot.day_of_week === schoolDayOfWeek(date)).sort((a, b) => a.period_name.localeCompare(b.period_name, undefined, { numeric: true })),
    [timetable.data, date],
  );
  useEffect(() => {
    if (lesson && !lessons.some((slot) => slot.id === lesson)) setLesson(null);
  }, [lesson, lessons]);

  const loaded = useMemo(() => (learners.data && saved.data ? registerDraft(learners.data, saved.data, lesson) : null), [learners.data, saved.data, lesson]);
  const [entries, setEntries] = useState<Record<number, RegisterEntry>>({});
  useEffect(() => {
    if (loaded) setEntries(loaded);
  }, [loaded]);

  const save = useMutation({
    mutationFn: () =>
      attendanceService.bulkMarkAttendance({
        ...(lesson ? { timetable_slot: lesson } : {}),
        attendance_records: (learners.data ?? []).map((learner) => ({ student_id: learner.id, date, status: entries[learner.id]?.status ?? "present", period: "full_day", notes: entries[learner.id]?.remarks ?? "" })),
      }),
    onSuccess: (data) => {
      setNotice({ tone: "success", text: `${data.message ?? "Register saved"}${lessonSlot ? ` for ${lessonSlot.subject_name}` : ""}. Anyone away several days running is flagged under Alerts.` });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
      onSaved();
    },
  });

  const lessonSlot = lessons.find((slot) => slot.id === lesson) ?? null;
  const rows = learners.data ?? [];
  const shown = rows.filter((row) => !search.trim() || `${row.full_name} ${row.student_id}`.toLowerCase().includes(search.trim().toLowerCase()));
  const counts = registerCounts(entries);
  const changes = loaded ? changedRows(loaded, entries) : 0;
  const taken = (saved.data ?? []).length > 0;
  const lastSaved = [...(saved.data ?? [])].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
  const set = (id: number, patch: Partial<RegisterEntry>) => setEntries((current) => ({ ...current, [id]: { ...current[id], ...patch } }));

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <section className="card">
        <div className="card-header pb-4">
          <h3 className="font-semibold text-gray-900">Which register</h3>
          <p className="mt-1 text-sm text-gray-500">Pick the day and class, or one of your lessons. A register taken before opens with its marks, so you can correct it.</p>
        </div>
        <div className="card-content space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Day">
              <input type="date" max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} className="input mt-1" />
            </Field>
            <Field label="Class">
              <select value={classId} onChange={(e) => { setClassId(Number(e.target.value)); setLesson(null); }} className="input mt-1">
                <option value={0}>{classes.isLoading ? "Loading classes…" : "Choose a class"}</option>
                {(classes.data ?? []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            </Field>
            <div className="self-end pb-2 text-sm text-gray-500">
              {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
          </div>
          {lessons.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700">Your lessons that day</p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                <button type="button" onClick={() => setLesson(null)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${!lesson ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:border-primary-300"}`}>
                  Whole-day register
                </button>
                {lessons.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => { setLesson(slot.id); setClassId(slot.class_assigned); }}
                    className={`rounded-lg border px-3 py-1.5 text-left text-xs ${lesson === slot.id ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:border-primary-300"}`}
                  >
                    <span className="block font-semibold">{slot.subject_name}</span>
                    <span className={lesson === slot.id ? "text-primary-100" : "text-gray-500"}>{slot.period_name} · {slot.class_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {classId > 0 && (
        <section className="card">
          <div className="card-header flex flex-wrap items-start justify-between gap-3 pb-4">
            <div>
              <h3 className="font-semibold text-gray-900">{lessonSlot ? `${lessonSlot.subject_name} · ${lessonSlot.class_name}, ${lessonSlot.period_name}` : `${classes.data?.find((row) => row.id === classId)?.name ?? "Class"} · whole day`}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {saved.isLoading ? "Checking for a saved register…" : taken ? `Taken before${lastSaved ? ` by ${lastSaved.marked_by_name || "a teacher"}, ${new Date(lastSaved.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}. Saving again updates it.` : "Not taken yet. Everyone starts present: change only those who are not."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {STATUSES.filter((row) => counts[row.value]).map((row) => (
                <span key={row.value} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700">
                  <span className={`h-1.5 w-1.5 rounded-full ${row.dot}`} /> {counts[row.value]} {row.label.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
          <div className="card-content space-y-3">
            {(learners.isError || saved.isError) && errorLine(learners.error ?? saved.error, "The class list could not be loaded.")}
            {rows.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="relative block w-full max-w-xs">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a learner" aria-label="Find a learner" className="input h-9 pl-8" />
                </label>
                <button type="button" onClick={() => loaded && setEntries(Object.fromEntries(Object.entries(loaded).map(([id, entry]) => [id, { ...entry, status: "present" as Status }])))} className="btn btn-outline h-9 gap-1.5 text-xs">
                  <RotateCcw className="h-3.5 w-3.5" /> Everyone present
                </button>
              </div>
            )}
            {learners.isLoading || saved.isLoading ? (
              <p className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">Loading the class list…</p>
            ) : rows.length === 0 ? (
              <p className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">No learners are in this class.</p>
            ) : (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Learner</th>
                      <th>Mark</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((learner, index) => {
                      const entry = entries[learner.id] ?? { status: "present" as Status, remarks: "" };
                      const other = !QUICK_STATUSES.includes(entry.status);
                      return (
                        <tr key={learner.id} className={entry.status === "present" ? undefined : "bg-amber-50/50"}>
                          <td className="sheet-rownum">{index + 1}</td>
                          <td className="min-w-[12rem]">
                            <span className="font-medium text-gray-900">{learner.full_name}</span>
                            <span className="block text-xs text-gray-500">{learner.student_id}</span>
                          </td>
                          <td>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <div className="inline-flex overflow-hidden rounded-md border border-gray-300" role="group" aria-label={`Mark for ${learner.full_name}`}>
                                {QUICK_STATUSES.map((value) => {
                                  const active = entry.status === value;
                                  const meta = statusOf(value);
                                  return (
                                    <button
                                      key={value}
                                      type="button"
                                      aria-pressed={active}
                                      onClick={() => set(learner.id, { status: value })}
                                      className={`inline-flex h-7 items-center gap-1 px-2.5 text-xs font-semibold [&:not(:first-child)]:border-l [&:not(:first-child)]:border-gray-300 ${active ? "bg-primary-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                    >
                                      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} aria-hidden />
                                      {meta.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <select
                                aria-label={`Other mark for ${learner.full_name}`}
                                value={other ? entry.status : ""}
                                onChange={(e) => e.target.value && set(learner.id, { status: e.target.value as Status })}
                                className={`input h-7 w-36 py-0 text-xs ${other ? "border-primary-500 font-semibold text-primary-800" : "text-gray-500"}`}
                              >
                                <option value="">Other…</option>
                                {STATUSES.filter((row) => !QUICK_STATUSES.includes(row.value)).map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}
                              </select>
                            </div>
                          </td>
                          <td className="min-w-[14rem]">
                            <input value={entry.remarks} onChange={(e) => set(learner.id, { remarks: e.target.value })} placeholder={entry.status === "present" ? "" : "e.g. Went home sick at 11"} aria-label={`Remark for ${learner.full_name}`} className="input h-7 py-0 text-xs" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {rows.length > 0 && (
              <div className="sticky bottom-0 z-20 pb-1 pt-2">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
                  <p className="text-sm text-gray-600">
                    <strong className="font-semibold text-gray-900">{rows.length}</strong> learner{rows.length === 1 ? "" : "s"}
                    {taken ? ` · ${changes ? `${changes} change${changes === 1 ? "" : "s"} to save` : "no changes"}` : " · not saved yet"}
                  </p>
                  <button type="button" disabled={save.isPending || (taken && !changes)} onClick={() => save.mutate()} className="btn btn-primary gap-1.5">
                    <CheckCircle2 className="h-4 w-4" /> {save.isPending ? "Saving…" : taken ? "Save changes" : lessonSlot ? "Save the lesson register" : "Save the register"}
                  </button>
                </div>
                {save.isError && <div className="mt-2">{errorLine(save.error, "The register could not be saved.")}</div>}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
